/**
 * MO Proxy API - RXPROXY API for prescriptions via Shopify App Proxy.
 * Requests go through `/apps/proxy/<action>`; Shopify signs them server-side.
 */
(function () {
  "use strict";

  window.MO = window.MO || {};

  var PROXY_BASE = "/apps/proxy";

  var PROXY_CONFIG = {
    SUBDOMAIN: "maskoptix"
  };

  function buildProxyUrl(action) {
    return PROXY_BASE + "/" + action;
  }

  function normalizeResponse(response) {
    if (!response) {
      return { success: false, error: "No response received" };
    }
    if (response.success && response.data && typeof response.data === "object") {
      return Object.assign({ success: true }, response.data, { data: response.data });
    }
    return response;
  }

  async function makeProxyRequest(action, payload) {
    try {
      var response = await fetch(buildProxyUrl(action), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(Object.assign({ action: action }, payload))
      });

      if (!response.ok) {
        var errorText = await response.text();
        throw new Error("HTTP " + response.status + ": " + errorText);
      }

      var data = await response.json();
      return normalizeResponse(data);
    } catch (error) {
      console.error("[RXPROXY] " + action + " failed:", error);
      return {
        success: false,
        error: error.message || "API request failed"
      };
    }
  }

  /**
   * Analyze prescription image using OCR
   */
  async function analyzePrescription(file) {
    if (!file) {
      return { success: false, error: "No file provided" };
    }

    try {
      var formData = new FormData();
      formData.append("action", "analyze-prescription");
      formData.append("prescription", file);

      var response = await fetch(buildProxyUrl("analyze-prescription"), {
        method: "POST",
        body: formData
      });

      if (!response.ok) {
        var errorText = await response.text();
        throw new Error("HTTP " + response.status + ": " + errorText);
      }

      var data = await response.json();
      var responseData = data.data || data;
      var uniqueId = responseData.uniqueId || responseData.unique_id;

      if (!uniqueId) {
        console.warn("[RXPROXY] No uniqueId in analyze response");
      }

      return {
        success: data.success || responseData.success || true,
        prescriptionData: responseData.prescriptionData,
        uniqueId: uniqueId,
        analysis: responseData.analysis,
        data: responseData
      };
    } catch (error) {
      console.error("[RXPROXY] analyzePrescription error:", error);
      return {
        success: false,
        error: error.message || "Failed to analyze prescription"
      };
    }
  }

  /**
   * Save prescription data
   */
  async function savePrescription(params) {
    var orderId = params.orderId;
    var lineItemId = params.lineItemId;
    var prescriptionData = params.prescriptionData;

    if (!orderId || !lineItemId || !prescriptionData) {
      return {
        success: false,
        error: "Missing required parameters: orderId, lineItemId, or prescriptionData"
      };
    }

    try {
      var payload = Object.assign({
        action: "save-prescription",
        order_id: orderId,
        line_item_id: lineItemId
      }, prescriptionData);

      var response = await fetch(buildProxyUrl("save-prescription"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        var errorText = await response.text();
        throw new Error("HTTP " + response.status + ": " + errorText);
      }

      var data = await response.json();
      var result = normalizeResponse(data);

      if (!result.success) return result;

      var rxUID = (result.data && result.data.rxUID) || result.rxUID;
      if (!rxUID) {
        console.warn("[RXPROXY] No rxUID in save response:", result);
      }

      return {
        success: true,
        data: Object.assign({ rxUID: rxUID }, result.data)
      };
    } catch (error) {
      console.error("[RXPROXY] savePrescription error:", error);
      return {
        success: false,
        error: error.message || "Failed to save prescription"
      };
    }
  }

  /**
   * Get order details
   */
  async function getOrder(orderId) {
    if (!orderId) {
      return { success: false, error: "No orderId provided" };
    }
    return makeProxyRequest("get-order", { orderId: orderId });
  }

  /**
   * Get prescription file URL
   */
  async function getPrescriptionFile(uniqueId) {
    if (!uniqueId) {
      return { success: false, error: "No uniqueId provided" };
    }
    return makeProxyRequest("get-prescription-file", { uniqueId: uniqueId });
  }

  /**
   * Update line item properties
   */
  async function updateLineItemProperties(params) {
    if (!params.orderId || !params.lineItemId || !params.properties) {
      return {
        success: false,
        error: "Missing required parameters: orderId, lineItemId, or properties"
      };
    }
    return makeProxyRequest("update-line-item-properties", {
      orderId: params.orderId,
      lineItemId: params.lineItemId,
      properties: params.properties
    });
  }

  window.MO.proxyApi = {
    analyzePrescription: analyzePrescription,
    savePrescription: savePrescription,
    getOrder: getOrder,
    getPrescriptionFile: getPrescriptionFile,
    updateLineItemProperties: updateLineItemProperties,
    config: PROXY_CONFIG
  };
})();
